/**
 * @file PlanPill.tsx
 * @brief The small "Free" / "Pro" / "Business" pill (user menu, Home header),
 *        and the quiet "Upgrade" link a Free personal user or company admin
 *        sees beside it. Never a pop-up or a banner (plans-visible brief A).
 *
 * @author DES
 * @date 21-10-2026
 */

import { Link } from "react-router-dom";
import { cn } from "@/shared/theme/cn";
import { plansHref, type PlanSummary } from "./planSummary";

export function PlanPill({ plan, className }: { plan: PlanSummary; className?: string }) {
  const paid = plan.planCode !== "free";
  return (
    <span
      className={cn(
        "inline-flex h-5 items-center rounded-full px-2 text-[11px] font-semibold normal-case leading-none tracking-normal",
        // The app's soft pair, so red on red stays readable in dark (21-10-2026).
        paid ? "bg-brand-soft text-brand-soft-foreground" : "bg-black/[0.06] text-app-text-secondary dark:bg-white/[0.08]",
        className,
      )}
      title={plan.audience === "company_member" ? `Your company is on ${plan.planName}` : `You're on ${plan.planName}`}
    >
      {plan.planName}
    </span>
  );
}

/** The pill, and for a Free account that can buy, a plain "Upgrade" link. */
export function PlanPillWithUpgrade({ plan, className }: { plan: PlanSummary; className?: string }) {
  const href = plansHref(plan);
  return (
    <span className={cn("inline-flex items-center gap-2", className)}>
      <PlanPill plan={plan} />
      {plan.planCode === "free" && href ? (
        <Link to={href} className="text-xs font-medium text-app-text-secondary underline-offset-2 hover:text-app-text hover:underline">
          Upgrade
        </Link>
      ) : null}
    </span>
  );
}
